//SocialLogs.jsx
import { useState, useEffect, useCallback } from "react";
import { Search, ShoppingBag, Package, ShoppingCart, SlidersHorizontal, X, Clock, ShieldCheck, Zap, Headphones } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { toast } from "react-toastify";
import { useWallet } from "../context/WalletContext";
import SocialProductCard   from "../components/SocialProductCard";
import SocialPurchaseModal from "../components/SocialPurchaseModal";
import MyPurchasesDrawer   from "../components/MyPurchasesDrawer";
import CartSidebar         from "../components/CartSidebar";


const DISCOUNT_THRESHOLD = 10;

// ── Post-purchase support notice — small side toast, not a modal ─────────────
function PurchaseSupportNotice() {
  return (
    <div className="flex items-start gap-2.5">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mt-0.5">
        <Clock size={13} className="text-amber-600 dark:text-amber-400" />
      </div>
      <div className="min-w-0 pr-1">
        <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white leading-snug">
          Check your log now
        </p>
        <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
          Report any issue to support within{" "}
          <span className="font-semibold text-amber-600 dark:text-amber-400">30 minutes</span>{" "}
          of purchase — complaints after this window can't be accepted.
        </p>
      </div>
    </div>
  );
}

function PurchaseSupportCloseButton({ closeToast }) {
  return (
    <button
      onClick={closeToast}
      className="flex-shrink-0 -mt-0.5 -mr-0.5 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
      aria-label="Dismiss"
    >
      <X size={14} />
    </button>
  );
}

function showPurchaseSupportNotice() {
  toast(<PurchaseSupportNotice />, {
    position:        "bottom-right",
    autoClose:       30000,
    closeOnClick:    false,
    pauseOnHover:    true,
    hideProgressBar: false,
    icon:            false,
    closeButton:     PurchaseSupportCloseButton,
    className:       "!bg-white dark:!bg-gray-900 !rounded-xl !shadow-lg !border !border-gray-200 dark:!border-gray-800 !min-h-0 !p-2.5 sm:!p-3 !mx-2 sm:!mx-0",
    bodyClassName:   "!p-0 !m-0",
    style:           { width: "260px", maxWidth: "calc(100vw - 32px)" },
  });
}

// ── Discount banner — now with a live progress bar tied to real cart count ───
function DiscountBanner({ cartCount }) {
  const [open, setOpen] = useState(false);
  const qualifies   = cartCount >= DISCOUNT_THRESHOLD;
  const itemsNeeded = Math.max(0, DISCOUNT_THRESHOLD - cartCount);
  const percent     = Math.min(100, (cartCount / DISCOUNT_THRESHOLD) * 100);

  return (
    <div className="mb-5 rounded-2xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base flex-shrink-0">🎁</span>
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 leading-tight">
            {qualifies ? (
              <>🎉 15% discount unlocked on your cart!</>
            ) : (
              <>Buy <span className="font-bold">10+</span> accounts and get{" "}
              <span className="text-amber-600 dark:text-amber-400 font-bold">15% OFF</span> instantly!</>
            )}
          </p>
        </div>
        <span className={`flex-shrink-0 w-5 h-5 rounded-full bg-amber-200 dark:bg-amber-700/60 flex items-center justify-center transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 3.5l3 3 3-3" stroke="#92400e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {/* Live progress bar — reflects the real cart, not a static message */}
      <div className="px-4 pb-3">
        <div className="w-full bg-amber-200/70 dark:bg-amber-800/40 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${qualifies ? "bg-green-500" : "bg-amber-500"}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1.5">
          {qualifies
            ? `${cartCount} items in cart — discount active`
            : `${cartCount}/${DISCOUNT_THRESHOLD} items — ${itemsNeeded} more unlocks 15% off`}
        </p>
      </div>

      {open && (
        <div className="px-4 pb-4 border-t border-amber-200 dark:border-amber-700/40 pt-3">
          <p className="text-xs text-amber-700 dark:text-amber-300 mb-2 font-medium">
            Mix any accounts — Facebook, Instagram, TikTok, X, or others:
          </p>
          <ol className="space-y-2">
            {[
              <>Click <strong>View</strong> on any card, select accounts, tap <strong>Add to Cart</strong>. Repeat for other cards.</>,
              <>When cart reaches <strong>10+ items</strong>, open <strong>Cart</strong> — discount applies automatically.</>,
              <>Review and pay. Under 10 items? No discount, but you can still buy normally.</>,
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-200 dark:bg-amber-700 flex items-center justify-center text-amber-900 dark:text-amber-100 font-bold text-xs mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ── Filter Drawer ─────────────────────────────────────────────────────────────
function FilterDrawer({ groups, activeCategory, onSelect, onClose }) {
  // Build category list from actual products
  const categoryMap = {};
  for (const g of groups) {
    const cat = (g.category || "other").toLowerCase();
    const label = getCategoryLabel(cat);
    if (!categoryMap[cat]) categoryMap[cat] = { cat, label, count: 0 };
    categoryMap[cat].count += g.stock ?? g.available ?? 0;
  }
  const categories = Object.values(categoryMap).sort((a, b) => b.count - a.count);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel — slides in from right */}
      <div className="relative ml-auto w-full max-w-xs bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">Filter</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Category list */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Categories
          </p>
          <div className="space-y-1">
            {/* All */}
            <button
              onClick={() => { onSelect(null); onClose(); }}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition ${
                activeCategory === null
                  ? "bg-purple-600 text-white"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <span>All Categories</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                activeCategory === null
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
              }`}>
                {groups.reduce((s, g) => s + (g.stock ?? g.available ?? 0), 0)}
              </span>
            </button>

            {categories.map(({ cat, label, count }) => (
              <button
                key={cat}
                onClick={() => { onSelect(cat); onClose(); }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition ${
                  activeCategory === cat
                    ? "bg-purple-600 text-white"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                <span>{label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                  activeCategory === cat
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                }`}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Clear button */}
        {activeCategory !== null && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={() => { onSelect(null); onClose(); }}
              className="w-full py-2.5 text-sm font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
            >
              Clear Filter
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function getCategoryLabel(cat) {
  const map = {
    facebook:    "Facebook",
    instagram:   "Instagram",
    tiktok:      "TikTok",
    twitter:     "Twitter/X",
    "twitter/x": "Twitter/X",
    x:           "Twitter/X",
    telegram:    "Telegram",
    vpn:         "VPN (Premium)",
    texting_app: "Texting Apps",
    google:      "Google",
    yahoo:       "Yahoo",
    outlook:     "Outlook",
    hotmail:     "Hotmail",
    whatsapp:    "WhatsApp",
    ip_proxy:    "IP & Proxy",
  };
  return map[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SocialLogs() {
  const navigate = useNavigate();

  const [groups,          setGroups]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState("");
  const [activeCategory,  setActiveCategory]  = useState(null);
  const [showFilter,      setShowFilter]      = useState(false);

  const [pendingOrder,    setPendingOrder]    = useState(null);
  const [purchasing,      setPurchasing]      = useState(false);
  const [purchaseError,   setPurchaseError]   = useState(null);

  const [showPurchases,   setShowPurchases]   = useState(false);
  const { walletBalance, refreshWallet } = useWallet();

  const [cart,     setCart]     = useState([]);
  const [showCart, setShowCart] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/social/accounts");
      if (res.data.success) setGroups(res.data.products || []);
    } catch {
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ── Filter + Search ─────────────────────────────────────────────────────────
  const filtered = groups.filter(g => {
    // Category filter
    if (activeCategory !== null) {
      const cat = (g.category || "").toLowerCase();
      if (cat !== activeCategory) return false;
    }

    // Search filter
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    const catLabel = (g.category || "").toLowerCase() === "texting_app"
      ? "texting app"
      : (g.category || "").toLowerCase();
    return (
      g.title?.toLowerCase().includes(term)       ||
      g.description?.toLowerCase().includes(term) ||
      g.category?.toLowerCase().includes(term)    ||
      catLabel.includes(term)                     ||
      g.country?.toLowerCase().includes(term)
    );
  });

  // ── Group filtered products by category, most-stocked category first ───────
  const groupedByCategory = (() => {
    const map = {};
    for (const g of filtered) {
      const cat = (g.category || "other").toLowerCase();
      if (!map[cat]) map[cat] = [];
      map[cat].push(g);
    }
    return Object.entries(map)
      .map(([cat, items]) => ({ cat, label: getCategoryLabel(cat), items }))
      .sort((a, b) => b.items.length - a.items.length);
  })();

  // ── Quick category chips — derived from real product data, top 6 by count ──
  const topCategories = (() => {
    const counts = {};
    for (const g of groups) {
      const cat = (g.category || "other").toLowerCase();
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .slice(0, 6);
  })();

  const handleBuy = order => {
  const isVaultAcc = order.group.source === "vaultacc";
  const isSujan    = order.group.source === "sujan";
  const isSameeha  = order.group.source === "sameeha";
  const isShopvia  = order.group.source === "shopvia";

  let totalQty;
  if (order.mode === "specific") {
    if (isSujan || isSameeha || isShopvia) totalQty = order.accountIds?.quantity || order.quantity || 1;
    else                                   totalQty = order.accountIds?.length || 1;
  } else {
    totalQty = order.quantity || 1;
  }

  const totalCost = order.group.priceNgn * totalQty;

  if (walletBalance < totalCost) {
    toast.error(
      `Insufficient balance — need ₦${totalCost.toLocaleString()}, you have ₦${walletBalance.toLocaleString()}. Please fund your wallet.`,
      { autoClose: 5000 }
    );
    setPurchaseError(null);
    setPendingOrder(order);
    return;
  }

  setPurchaseError(null);
  setPendingOrder(order);
};

  const confirmPurchase = async () => {
  if (!pendingOrder) return null;
  setPurchasing(true);
  setPurchaseError(null);

  try {
    const { group, mode, quantity, accountIds } = pendingOrder;
    const isSujan    = group.source === "sujan";
    const isSameeha  = group.source === "sameeha";
    const isShopvia  = group.source === "shopvia";
    const isVaultAcc = group.source === "vaultacc";

    let body;

    if (isSujan) {
      const qty = pendingOrder.mode === "specific"
        ? (pendingOrder.sujan
            ? pendingOrder.accountIds?.quantity || pendingOrder.quantity || 1
            : pendingOrder.accountIds?.length || 1)
        : pendingOrder.quantity || 1;
      body = {
        groupKey:  group.groupKey,
        quantity:  qty,
        unitPrice: group.priceNgn,
        title:     group.title,
        category:  group.category,
      };

    } else if (isSameeha) {
      const qty = pendingOrder.mode === "specific"
        ? (pendingOrder.sameeha
            ? pendingOrder.accountIds?.quantity || pendingOrder.quantity || 1
            : pendingOrder.accountIds?.length || 1)
        : pendingOrder.quantity || 1;
      body = {
        groupKey:  group.groupKey,
        quantity:  qty,
        unitPrice: group.priceNgn,
        title:     group.title,
        category:  group.category,
      };

    } else if (isShopvia) {
      const qty = pendingOrder.mode === "specific"
        ? (pendingOrder.shopvia
            ? pendingOrder.accountIds?.quantity || pendingOrder.quantity || 1
            : pendingOrder.accountIds?.length || 1)
        : pendingOrder.quantity || 1;
      body = {
        groupKey:  group.groupKey,
        quantity:  qty,
        unitPrice: group.priceNgn,
        title:     group.title,
        category:  group.category,
      };

    } else if (isVaultAcc) {
      const qty = pendingOrder.mode === "specific"
        ? (pendingOrder.accountIds?.length || 1)
        : pendingOrder.quantity || 1;

      if (pendingOrder.mode === "specific" && Array.isArray(pendingOrder.accountIds) && pendingOrder.accountIds.length > 0) {
        // User selected specific items from the View modal
        body = {
          vaultaccLogIds: pendingOrder.accountIds.map(Number),
          unitPrice:      group.priceNgn,
          title:          group.title,
          category:       group.category,
        };
      } else {
        // Bulk buy from card's Buy Now button — use groupKey + quantity
        body = {
          groupKey:  group.groupKey,
          quantity:  qty,
          unitPrice: group.priceNgn,
          title:     group.title,
          category:  group.category,
        };
      }

    } else {
      // Manual accounts
      const qty = pendingOrder.mode === "specific"
        ? (pendingOrder.accountIds?.length || 1)
        : pendingOrder.quantity || 1;

      if (pendingOrder.mode === "specific" && Array.isArray(pendingOrder.accountIds) && pendingOrder.accountIds.length > 0) {
        body = { accountIds: pendingOrder.accountIds };
      } else {
        body = { groupKey: group.groupKey, quantity: qty };
      }
    }

    const res = await api.post("/social/buy", body);

    if (res.data.success) {
      const count = res.data.purchases.length;
      toast.success(`${count} item${count !== 1 ? "s" : ""} purchased!`);
      refreshWallet();       // fixes the undefined setWalletBalance bug
      fetchProducts();
      setPendingOrder(null); // closes the purchase modal
      setPurchaseError(null);
      setShowPurchases(true); // opens My Purchases drawer immediately
      showPurchaseSupportNotice(); // small side toast — 30-min complaint window
      return res.data.purchases;
    }

    const msg = res.data?.message || "Purchase failed";
    setPurchaseError(msg);
    return null;
  } catch (err) {
    const msg = err.response?.data?.message || "Purchase failed";
    setPurchaseError(msg);
    toast.error(msg);
    return null;
  } finally {
    setPurchasing(false);
  }
};

  const handleAddToCart = (cartItems) => {
    setCart(prev => {
      const existingIds = new Set(prev.map(i => i.cartId));
      const newItems    = cartItems.filter(i => !existingIds.has(i.cartId));
      const updated     = [...prev, ...newItems];
      const count       = newItems.length;
      if (count > 0) {
        toast.success(
          `${count} item${count !== 1 ? "s" : ""} added to cart! ${updated.length >= DISCOUNT_THRESHOLD ? "🎉 15% discount unlocked!" : `(${updated.length}/${DISCOUNT_THRESHOLD} for 15% off)`}`,
          { autoClose: 3000 }
        );
      }
      return updated;
    });
  };

  const handleRemoveCartItem = (cartId) => {
    setCart(prev => prev.filter((_, i) => (prev[i]?.cartId || i) !== cartId));
  };

  const handleClearCart = () => setCart([]);

  const handleCartPurchaseSuccess = (newBalance) => {
    refreshWallet();
    fetchProducts();
    setCart([]);
    setShowCart(false);     // closes the cart sidebar
    setShowPurchases(true); // opens My Purchases drawer immediately
    showPurchaseSupportNotice(); // small side toast — 30-min complaint window
  };

  const cartCount = cart.length;
  const isFiltered = activeCategory !== null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

      {/* ── Editorial hero ── */}
      <div className="pt-1 pb-5 mb-5 border-b border-gray-100 dark:border-gray-800 text-center">
        <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400 mb-2">
          Market Place
        </p>
        <h1 className="font-serif text-xl sm:text-3xl font-normal text-gray-900 dark:text-white leading-snug mb-2 px-4 sm:px-0">
          Verified accounts, ready in seconds
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
          Social accounts, VPN &amp; texting apps — instant delivery, every time.
        </p>

        <div className="flex justify-center gap-2 flex-wrap mb-4">
          <span className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-2.5 sm:px-3 py-1.5 rounded-full">
            <ShieldCheck size={12} className="text-green-500 flex-shrink-0" /> Verified logs
          </span>
          <span className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-2.5 sm:px-3 py-1.5 rounded-full">
            <Zap size={12} className="text-amber-500 flex-shrink-0" /> Instant delivery
          </span>
          <span className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-2.5 sm:px-3 py-1.5 rounded-full">
            <Headphones size={12} className="text-purple-500 flex-shrink-0" /> 24/7 support
          </span>
        </div>

        <div className="flex justify-center gap-2 px-4 sm:px-0">
          <button
            onClick={() => setShowCart(true)}
            className="relative flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 sm:px-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 text-gray-700 dark:text-gray-200 text-xs sm:text-sm font-medium rounded-xl transition"
          >
            <ShoppingCart size={15} />
            Cart
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-purple-600 text-white text-[10px] font-bold rounded-full">
                {cartCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowPurchases(true)}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 sm:px-5 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm font-medium rounded-xl transition"
          >
            <Package size={15} />My Purchases
          </button>
        </div>
      </div>

      <DiscountBanner cartCount={cartCount} />

      {/* Search + Filter row */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search accounts, VPN, texting apps..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>

        {/* Filter button */}
        <button
          onClick={() => setShowFilter(true)}
          className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition flex-shrink-0 ${
            isFiltered
              ? "bg-purple-600 text-white border-purple-600"
              : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700"
          }`}
        >
          <SlidersHorizontal size={16} />
          <span className="hidden sm:inline">Filter</span>
          {isFiltered && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-400 rounded-full text-xs font-bold text-gray-900 flex items-center justify-center">
              1
            </span>
          )}
        </button>
      </div>

      {/* Quick category chips — one-tap filtering, built from real product data */}
      {topCategories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 mb-5 -mx-4 px-4 sm:mx-0 sm:px-0">
          <button
            onClick={() => setActiveCategory(null)}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
              activeCategory === null
                ? "bg-purple-600 text-white"
                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
            }`}
          >
            All
          </button>
          {topCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                activeCategory === cat
                  ? "bg-purple-600 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-300"
              }`}
            >
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>
      )}

      {/* Active filter badge */}
      {isFiltered && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500 dark:text-gray-400">Filtered by:</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full">
            {getCategoryLabel(activeCategory)}
            <button
              onClick={() => setActiveCategory(null)}
              className="hover:text-purple-900 dark:hover:text-purple-100 transition"
            >
              <X size={12} />
            </button>
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            ({filtered.length} result{filtered.length !== 1 ? "s" : ""})
          </span>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <ShoppingBag size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">No results found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            {search.trim()
              ? `Nothing matched "${search}" — try a different search`
              : isFiltered
              ? `No products in this category right now`
              : "Check back later for new products"}
          </p>
          {isFiltered && (
            <button
              onClick={() => setActiveCategory(null)}
              className="mt-4 px-4 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
            >
              Clear Filter
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {groupedByCategory.map(({ cat, label, items }) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-1 h-5 bg-purple-600 rounded-full" />
                <h2 className="font-serif text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {label}
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(group => (
                  <SocialProductCard
                    key={group.groupId || group.groupKey}
                    group={group}
                    onBuy={handleBuy}
                    onAddToCart={handleAddToCart}
                    cart={cart}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Purchase Modal */}
      {pendingOrder && (
        <SocialPurchaseModal
          group={pendingOrder.group}
          quantity={
            pendingOrder.mode === "specific"
              ? (pendingOrder.sujan
                  ? pendingOrder.accountIds?.quantity || pendingOrder.quantity || 1
                  : pendingOrder.accountIds?.length || 1)
              : pendingOrder.quantity || 1
          }
          walletBalance={walletBalance}
          purchasing={purchasing}
          purchaseError={purchaseError}
          onConfirm={confirmPurchase}
          onClose={() => { setPendingOrder(null); setPurchaseError(null); }}
          onGoToWallet={() => {
            setPendingOrder(null);
            setPurchaseError(null);
            navigate("/dashboard/wallet");
          }}
        />
      )}

      {/* My Purchases Drawer */}
      {showPurchases && (
        <MyPurchasesDrawer onClose={() => setShowPurchases(false)} />
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <CartSidebar
          cart={cart}
          onClose={() => setShowCart(false)}
          onRemoveItem={handleRemoveCartItem}
          onClearCart={handleClearCart}
          walletBalance={walletBalance}
          onPurchaseSuccess={handleCartPurchaseSuccess}
        />
      )}

      {/* Filter Drawer */}
      {showFilter && (
        <FilterDrawer
          groups={groups}
          activeCategory={activeCategory}
          onSelect={setActiveCategory}
          onClose={() => setShowFilter(false)}
        />
      )}

    </div>
  );
}